import React, {useState, useEffect} from 'react'
import {httpGet, httpPost} from '../shared/Config';
import {Button} from 'reactstrap';

import { useTranslation } from 'react-i18next';
import SimpleModal from "../shared/SimpleModal";
import InputSelect from "../shared/InputSelect";
import InputText from "../shared/InputText";
import * as XLSX from 'xlsx';

import Table from '../shared/Table'

const defaultData = {
    certData: '',
    email: '',
    type: ''
}

const DataTypes = {
    RFC: 'RFC',
    EMAIL: 'Correo',
    PHONE: 'Teléfono móvil'
}

function MassiveUsersModal({isOpen, onCancel, onSaved, orgId, planData, firstUser, existingUsers}) {

    const {t} = useTranslation();

    const [working, setWorking] = useState(false);

    const [users, setUsers] = useState([]);
    const [serverConf, setServerConf] = useState({});
    const [documentAdded, setDocumentAdded] = useState(false);
    const [error, setError] = useState("");

    const [processCompleted, setProcessCompleted] = useState(false);
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        async function fetchServerConf() {
            try {
                const response = await httpGet('/getServerConf');
                if(response?.data.jsonData) {
                    let parseConf = JSON.parse(response.data.jsonData);
                    setServerConf(parseConf);
                }
            } catch(e) {
                console.error(e);
            }
        }
        fetchServerConf();
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        e.stopPropagation();
        setWorking(true);

        for(let index = 0; index < users.length; index++) {
            // we loop through each of them
            const postData = {
                type: users[index].TYPE,
                email: users[index].EMAIL
            }
            if(users[index].TYPE === 'email') {
                postData.certData = users[index].EMAIL;
            } else if(users[index].TYPE === 'sat') {
                postData.certData = users[index].RFC;
            }
            const response = await httpPost(`/orgs/${orgId}/users/add`, postData);

            let status = "";
            let error = false;
            if(response?.data) {
                if(response?.data.success) {
                    status = "COMPLETED";
                } else {
                    if(response.data.error) {
                        status = response.data.error;
                    } else {
                        status = "ERROR_USER";
                    }
                    error = true;
                }
            } else {
                status = "ERROR_USER";
                error = true;
            }
            
            setUsers(prevUsers => {
                const newUserState = [...prevUsers];
                newUserState[index] = {...newUserState[index], status: status, error: error}
                return newUserState;
            });
        
            await delay(500);
        }
        setWorking(false);
        setProcessCompleted(true);
    }

    function handleCancel() {
        setUsers([]);
        setError("");
        setProcessCompleted(false);
        setDocumentAdded(false);
        onCancel();
    }

    
    function handleClose() {
        setUsers([]);
        setError("");
        setProcessCompleted(false);
        setDocumentAdded(false);
        onSaved();
    }

    function hasDuplicates(array) {
        const emails = new Set();
        const rfc = new Set();
      
        for (const item of array) {
          if (item.email) {
            if (emails.has(item.email)) {
              return true;
            }
            emails.add(item.email);
          }
      
          if (item.rfc && item.rfc.trim() !== "") {
            if (rfc.has(item.rfc)) {
              return true;
            }
            rfc.add(item.rfc);
          }
        }
        return false; 
    }

    function isEmail(email) {
        if (typeof email !== 'string' && email !== "") return false;
        const reg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return reg.test(email);
    }

    function isRFC(rfc) {
        if (typeof rfc !== 'string' && rfc !== "") return false;
        const reg = /^[a-zA-ZÑñ&]{3,4}[0-9]{6}[a-zA-Z0-9]{3,5}$/;
        return reg.test(rfc);
    } 
    
    const handleFileUpload = (e) => {
        setError("");
        setUsers([]);
        setDocumentAdded(false);

        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();

        reader.readAsArrayBuffer(file);

        reader.onload = (event) => {
            const buffer = event.target.result;
            const workbook = XLSX.read(buffer, {type:'buffer'});
            const fSheet = workbook.SheetNames[0];
            const sheet = workbook.Sheets[fSheet];
            let data = XLSX.utils.sheet_to_json(sheet);

            if(data.length === 0) {
                setError("NO_USERS_DETECTED");
                return;
            } else if(data.length > 50) {
                setError("TOO_MANY_USERS");
                return;
            }

            if(hasDuplicates(data)) {
                setError("REPEATED_DATA");
                return;
            }

            // TODO: Check if it doesn't repeat with values already on the list
            for(let user of data) {
                if(!user.TYPE) {
                    setError("MISSING_TYPE")
                    return;
                } 

                let typeCheck = user.TYPE.toUpperCase();
                if(typeCheck !== "EMAIL" && typeCheck !== "SAT") {
                    setError("TYPE_WRONG");
                    return;
                }

                if(!isEmail(user.EMAIL)) {
                    setError("NOT_AN_EMAIL");
                    return;
                }

                if(user.TYPE === "sat" && !isRFC(user.RFC)) {
                    setError("NOT_AN_RFC");
                    return;
                }

                user.status = "PENDING";
                user.error = false;
            }

            setUsers(data);
            setDocumentAdded(true);
        };
    }

    return (
        <SimpleModal isOpen={isOpen}>
            <form onSubmit={handleSubmit}>
                <h3>
                    {t('ADD_MASSIVE_USERS')}
                </h3>
                {t('MASSIVE_USERS_DESC')}
                <br/>
                <img src="https://storage.googleapis.com/static.firmamex.com/ejemploDoc2.png"></img>
                <input 
                    type="file" 
                    accept=".xlsx, .xls" 
                    onChange={handleFileUpload} 
                    style={{ marginBottom: '20px' }}
                />
                <br/>
                {
                    users ? <React.Fragment>
                        {users.length}/50
                    </React.Fragment> : null
                }
                {
                    documentAdded ? <React.Fragment>
                        <Table collection={users} noData="NO_USERS_FOUND">
                            <thead>
                                <tr>
                                    <th>{t('TYPE')}</th>
                                    <th>{t('EMAIL')}</th>
                                    <th>{t('RFC')}</th>
                                    <th>{t('STATUS')}</th>
                                </tr>
                            </thead> 
                            <tbody>
                                {
                                    users.map((u, i) => (
                                        <tr key={i}>
                                            <td>
                                                <span style={{textTransform: 'uppercase'}}>{u.TYPE}</span>
                                            </td>
                                            <td>
                                                {u.EMAIL}
                                            </td>
                                            <td>
                                                {
                                                    u.RFC ? <React.Fragment>
                                                        {u.RFC}
                                                    </React.Fragment> : null
                                                }
                                            </td>
                                            <td style={{color: u.error ? 'red' : null, fontWeight: 'bold'}}>
                                                {t(u.status)}
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>   
                        </Table>
                    </React.Fragment> : null
                }
                <br/>
                {
                    error ? <React.Fragment>
                        <span style={{color: 'red', fontWeight: 'bold'}}>{t(error)}</span>
                    </React.Fragment> : null
                }

                <div className={'modal-action'}>
                    <Button type="submit" disabled={working || users.length <= 0 || processCompleted} color="primary">{t('SAVE')}</Button>
                    {
                        processCompleted ? <React.Fragment>
                            <Button disabled={working} color="secondary" onClick={handleClose}>{t('CLOSE')}</Button>
                        </React.Fragment> : <React.Fragment>
                            <Button disabled={working || processCompleted} color="secondary" onClick={handleCancel}>{t('CANCEL')}</Button>
                        </React.Fragment>
                    }
                </div>
            </form>
        </SimpleModal>
    )
}

export default MassiveUsersModal;
